import { idDaPergunta } from './crm-listas';

/**
 * CRM lote 4 (28/10, N1): o questionário no estilo do Google Formulários, regras puras da tela (o editor em cartões, a resposta
 * na ficha e o resumo das respostas coletadas). O servidor confere tudo de novo (`modules/pipeline/src/oportunidade/questionario.ts`);
 * aqui as mesmas regras dão o aviso antes de enviar. A API já devolve o formato antigo traduzido para o novo.
 */

export const TIPOS = ['curta', 'paragrafo', 'multipla', 'caixas', 'lista', 'escala', 'data', 'hora', 'numero'] as const;
export type TipoResposta = (typeof TIPOS)[number];
export type TipoPergunta = TipoResposta | 'secao';
export const ESCOLHA: ReadonlySet<string> = new Set(['multipla', 'caixas', 'lista']);
export const COM_OUTRO: ReadonlySet<string> = new Set(['multipla', 'caixas']);
export const LIMITE_ITENS = 60;
export const LIMITE_OPCOES = 50;

export interface Escala {
  min: number;
  max: number;
  rotulo_min: string;
  rotulo_max: string;
}
export interface Pergunta {
  id: string;
  texto: string;
  descricao: string;
  tipo: TipoPergunta;
  opcoes: string[];
  pontos: number[];
  outro: boolean;
  obrigatoria: boolean;
  escala?: Escala;
  legado?: 'sim_nao';
}
export interface Questionario {
  id: string;
  nome: string;
  descricao: string;
  perguntas: Pergunta[];
  ativo: boolean;
  /** CRM lote 5 (X1): qualificar a negociação pela pontuação, pelas faixas. */
  qualificar_auto?: boolean;
  faixas?: FaixaQualificacao[];
  respostas?: number;
  updated_at?: string;
}
export interface FaixaQualificacao {
  min: number;
  max: number;
  estrelas: number;
}
export type Valor = string | number | boolean | string[] | null;
export interface RespostaColetada {
  id: string;
  oportunidade_id: string;
  oportunidade_titulo: string;
  /** Rodada rlote-8: o cliente da negociação, para o modo discreto do título. */
  parceiro_nome?: string | null;
  respondido_por_nome: string | null;
  respondido_em: string;
  respostas: Record<string, Valor>;
  pontuacao: number | null;
}

// ---------------------------------------------------------------------------------------------------------------- editor

/** Pergunta em edição: `chave` só para a tela (React e foco); `id` vazio na pergunta nova (sai do texto ao salvar). */
export type Rascunho = Pergunta & { chave: string };

const chave = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : Math.random().toString(36).slice(2));
export const ESCALA_PADRAO: Escala = { min: 1, max: 5, rotulo_min: '', rotulo_max: '' };

export const paraRascunho = (p: Pergunta): Rascunho => ({ ...p, descricao: p.descricao ?? '', pontos: p.pontos ?? [], outro: !!p.outro, chave: chave() });

/** Pergunta nova como no Google Formulários: Múltipla escolha com uma opção. */
export function novaPergunta(rotuloOpcao: string, tipo: TipoPergunta = 'multipla'): Rascunho {
  return { id: '', chave: chave(), texto: '', descricao: '', tipo, opcoes: ESCOLHA.has(tipo) ? [rotuloOpcao] : [], pontos: [], outro: false, obrigatoria: false, escala: tipo === 'escala' ? { ...ESCALA_PADRAO } : undefined };
}

/** Troca o tipo guardando o que dá para guardar (as opções entre os tipos de escolha, o "Outro" onde cabe). */
export function mudarTipo(p: Rascunho, tipo: TipoPergunta, rotuloOpcao: string): Rascunho {
  const escolha = ESCOLHA.has(tipo);
  return {
    ...p,
    tipo,
    opcoes: escolha ? (p.opcoes.length ? p.opcoes : [rotuloOpcao]) : p.opcoes,
    outro: COM_OUTRO.has(tipo) && p.outro,
    escala: tipo === 'escala' ? (p.escala ?? { ...ESCALA_PADRAO }) : p.escala,
    obrigatoria: tipo === 'secao' ? false : p.obrigatoria,
  };
}

export function mover<T>(lista: T[], i: number, delta: number): T[] {
  const j = i + delta;
  if (i < 0 || i >= lista.length || j < 0 || j >= lista.length) return lista;
  const nova = [...lista];
  [nova[i], nova[j]] = [nova[j]!, nova[i]!];
  return nova;
}

/** Cópia logo abaixo, com id novo (sai do texto ao salvar) e as mesmas opções. */
export function duplicar(lista: Rascunho[], i: number): Rascunho[] {
  const p = lista[i];
  if (!p) return lista;
  const copia: Rascunho = { ...p, id: '', chave: chave(), opcoes: [...p.opcoes], pontos: [...p.pontos], escala: p.escala ? { ...p.escala } : undefined, legado: undefined };
  return [...lista.slice(0, i + 1), copia, ...lista.slice(i + 1)];
}

/** Opções: mover junto com os pontos. */
export function moverOpcao(p: Rascunho, i: number, delta: number): Rascunho {
  const pontos = p.opcoes.map((_, k) => p.pontos[k] ?? 0);
  return { ...p, opcoes: mover(p.opcoes, i, delta), pontos: mover(pontos, i, delta) };
}
export function removerOpcao(p: Rascunho, i: number): Rascunho {
  return { ...p, opcoes: p.opcoes.filter((_, k) => k !== i), pontos: p.pontos.filter((_, k) => k !== i) };
}

export interface TextosEditor {
  obrigatorio: string;
  semOpcoes: string;
  opcaoRepetida: (o: string) => string;
  semPerguntas: string;
}

/** Erros do editor: `nome`, `geral`, `p<chave>` (título da pergunta) e `o<chave>` (opções). */
export function validarEditor(nome: string, lista: Rascunho[], tx: TextosEditor): Record<string, string> {
  const e: Record<string, string> = {};
  if (!nome.trim()) e.nome = tx.obrigatorio;
  if (!lista.some((p) => p.tipo !== 'secao')) e.geral = tx.semPerguntas;
  for (const p of lista) {
    if (!p.texto.trim()) e[`p${p.chave}`] = tx.obrigatorio;
    if (!ESCOLHA.has(p.tipo)) continue;
    const opcoes = p.opcoes.map((o) => o.trim()).filter(Boolean);
    if (!opcoes.length) e[`o${p.chave}`] = tx.semOpcoes;
    const vistos = new Set<string>();
    for (const o of opcoes) {
      const k = o.toLocaleLowerCase('pt-BR');
      if (vistos.has(k)) {
        e[`o${p.chave}`] = tx.opcaoRepetida(o);
        break;
      }
      vistos.add(k);
    }
  }
  return e;
}

/** O corpo que a API recebe: ids saídos do texto (sem repetir), opções limpas com os pontos alinhados, só o que o tipo usa. */
export function paraEnvio(lista: Rascunho[]): Pergunta[] {
  const usados = new Set(lista.filter((p) => p.id).map((p) => p.id));
  return lista.map((p) => {
    const escolha = ESCOLHA.has(p.tipo);
    const pares = escolha ? p.opcoes.map((o, k) => [o.trim(), p.pontos[k] ?? 0] as const).filter(([o]) => o) : [];
    const pontos = pares.map(([, n]) => (Number.isFinite(n) ? arredondarPontos(n) : 0));
    return {
      id: p.id || idDaPergunta(p.texto, usados),
      texto: p.texto.trim(),
      descricao: p.descricao.trim(),
      tipo: p.tipo,
      opcoes: pares.map(([o]) => o),
      pontos: pontos.some((n) => n) ? pontos : [],
      outro: COM_OUTRO.has(p.tipo) && p.outro,
      obrigatoria: p.tipo !== 'secao' && p.obrigatoria,
      ...(p.tipo === 'escala' ? { escala: p.escala ?? ESCALA_PADRAO } : {}),
      ...(escolha && p.legado ? { legado: p.legado } : {}),
    };
  });
}

/** Número da pergunta como o vendedor vê (as seções não contam). */
export function numeracao(lista: Pick<Pergunta, 'tipo'>[]): number[] {
  let n = 0;
  return lista.map((p) => (p.tipo === 'secao' ? 0 : ++n));
}

// ---------------------------------------------------------------------------------------------------------------- resposta

export const vazio = (v: Valor | undefined) => v === undefined || v === null || (typeof v === 'string' && !v.trim()) || (Array.isArray(v) && !v.length);
const DATA = /^\d{4}-\d{2}-\d{2}$/;
const HORA = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Aviso antes de enviar (o servidor confere de novo): `obrigatoria` ou `invalida` por pergunta. */
export function conferirRespostas(perguntas: Pergunta[], r: Record<string, Valor>): Record<string, 'obrigatoria' | 'invalida'> {
  const e: Record<string, 'obrigatoria' | 'invalida'> = {};
  for (const p of perguntas) {
    if (p.tipo === 'secao') continue;
    const v = r[p.id];
    if (vazio(v)) {
      if (p.obrigatoria) e[p.id] = 'obrigatoria';
      continue;
    }
    const ok =
      p.tipo === 'curta' || p.tipo === 'paragrafo'
        ? typeof v === 'string'
        : p.tipo === 'multipla' || p.tipo === 'lista'
          ? typeof v === 'string' && (p.opcoes.includes(v) || p.outro)
          : p.tipo === 'caixas'
            ? Array.isArray(v) && v.filter((x) => !p.opcoes.includes(x)).length <= (p.outro ? 1 : 0)
            : p.tipo === 'escala'
              ? typeof v === 'number' && Number.isInteger(v) && v >= (p.escala?.min ?? 1) && v <= (p.escala?.max ?? 5)
              : p.tipo === 'numero'
                ? typeof v === 'number' && Number.isFinite(v)
                : p.tipo === 'data'
                  ? typeof v === 'string' && DATA.test(v)
                  : typeof v === 'string' && HORA.test(v);
    if (!ok) e[p.id] = 'invalida';
  }
  return e;
}

/** Só as respostas das perguntas (as de seção e as vazias saem). */
export function respostasParaEnvio(perguntas: Pergunta[], r: Record<string, Valor>): Record<string, Valor> {
  const s: Record<string, Valor> = {};
  for (const p of perguntas) {
    if (p.tipo === 'secao' || vazio(r[p.id])) continue;
    const v = r[p.id]!;
    s[p.id] = typeof v === 'string' ? v.trim() : v;
  }
  return s;
}

/** RCRM5-ROB-B2: pontos e somas com no máximo 2 casas (como o servidor), sem o resto do ponto flutuante. */
export const arredondarPontos = (n: number) => Math.round(n * 100) / 100;

/** Casas decimais em uso (0 a 2) de um número já arredondado. */
const casas = (n: number) => (Number.isInteger(n) ? 0 : Number.isInteger(n * 10) ? 1 : 2);

/** Soma dos pontos (como o servidor); `null` quando o questionário não dá pontos. */
export function pontuacao(perguntas: Pergunta[], r: Record<string, Valor>): number | null {
  let tem = false;
  let soma = 0;
  for (const p of perguntas) {
    if (!ESCOLHA.has(p.tipo) || !p.pontos.some((x) => x)) continue;
    tem = true;
    const v = r[p.id];
    for (const e of Array.isArray(v) ? v : typeof v === 'string' ? [v] : []) {
      const i = p.opcoes.indexOf(e);
      if (i >= 0) soma += p.pontos[i] ?? 0;
    }
  }
  return tem ? arredondarPontos(soma) : null;
}

// ---------------------------------------------------------------------------------------------------------------- respostas coletadas

export type ResumoPergunta =
  | { tipo: 'opcoes'; total: number; opcoes: { rotulo: string; n: number; pct: number }[]; outros: string[] }
  | { tipo: 'escala'; total: number; media: number | null; valores: { rotulo: string; n: number; pct: number }[] }
  | { tipo: 'numero'; total: number; media: number | null; min: number | null; max: number | null }
  | { tipo: 'textos'; total: number; textos: string[] };

/**
 * Resumo por pergunta, como a aba Respostas do Google Formulários: nas de escolha, quantas vezes cada opção (o percentual é sobre
 * quem respondeu a pergunta; nas caixas a soma passa de 100%) e os textos do "Outro"; na escala, a distribuição e a média; no
 * número, média, menor e maior; nas de texto, data e hora, a lista.
 */
export function resumir(p: Pergunta, itens: Pick<RespostaColetada, 'respostas'>[]): ResumoPergunta {
  const valores = itens.map((i) => i.respostas[p.id]).filter((v): v is Exclude<Valor, null> => !vazio(v));
  const total = valores.length;
  const pct = (n: number) => (total ? n / total : 0);
  if (ESCOLHA.has(p.tipo)) {
    const contagem = new Map(p.opcoes.map((o) => [o, 0]));
    const outros: string[] = [];
    for (const v of valores) {
      for (const x of Array.isArray(v) ? v : [String(v)]) {
        if (contagem.has(x)) contagem.set(x, contagem.get(x)! + 1);
        else outros.push(x);
      }
    }
    const opcoes = [...contagem].map(([rotulo, n]) => ({ rotulo, n, pct: pct(n) }));
    return { tipo: 'opcoes', total, opcoes, outros };
  }
  const numeros = valores.filter((v): v is number => typeof v === 'number');
  const media = numeros.length ? numeros.reduce((a, b) => a + b, 0) / numeros.length : null;
  if (p.tipo === 'escala') {
    const e = p.escala ?? ESCALA_PADRAO;
    const lista = Array.from({ length: e.max - e.min + 1 }, (_, k) => e.min + k).map((n) => {
      const c = numeros.filter((x) => x === n).length;
      return { rotulo: String(n), n: c, pct: pct(c) };
    });
    return { tipo: 'escala', total, media, valores: lista };
  }
  if (p.tipo === 'numero') return { tipo: 'numero', total, media, min: numeros.length ? Math.min(...numeros) : null, max: numeros.length ? Math.max(...numeros) : null };
  return { tipo: 'textos', total, textos: valores.map((v) => (Array.isArray(v) ? v.join(', ') : String(v))) };
}

/** Valor da resposta em texto (CSV e lista individual): caixas separadas por vírgula; o sim/não antigo já vem como opção. */
export function textoDoValor(v: Valor | undefined): string {
  if (vazio(v)) return '';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}

/** Linhas do CSV: cabeçalho fixo (negociação, quem, quando, pontuação) e uma coluna por pergunta (as seções não entram). */
export function linhasCsv(q: Pick<Questionario, 'perguntas'>, itens: RespostaColetada[], cab: [string, string, string, string], quando: (iso: string) => string): (string | number | null)[][] {
  const perguntas = q.perguntas.filter((p) => p.tipo !== 'secao');
  return [
    [...cab, ...perguntas.map((p) => p.texto)],
    ...itens.map((i) => [i.oportunidade_titulo, i.respondido_por_nome ?? '', quando(i.respondido_em), i.pontuacao, ...perguntas.map((p) => textoDoValor(i.respostas[p.id]))]),
  ];
}

/** Nome do arquivo sem caracteres que o sistema de arquivos recusa. */
export const nomeDoArquivo = (nome: string) => nome.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-zA-Z0-9-]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase().slice(0, 60) || 'questionario';

// ---------------------------------------------------------------------------------------------------------------- qualificação automática

/**
 * CRM lote 5 (28/10, X1): qualificação automática pela pontuação, opcional por questionário. As mesmas regras do servidor
 * (`questionario.ts`: `limitesPontuacao`, `conferirFaixas`, `estrelasDaPontuacao`) dão o aviso antes de salvar.
 */
export const LIMITE_FAIXAS = 10;

/** Faixa em edição: os limites como o texto digitado (o número sai pelo `ler` do idioma). */
export interface FaixaRascunho {
  chave: string;
  min: string;
  max: string;
  estrelas: number;
}

/** Menor e maior pontuação possíveis; `null` quando nenhuma pergunta dá pontos. */
export function limitesPontuacao(perguntas: Pick<Pergunta, 'tipo' | 'pontos'>[]): { min: number; max: number } | null {
  let tem = false;
  let min = 0;
  let max = 0;
  for (const p of perguntas) {
    if (!ESCOLHA.has(p.tipo) || !p.pontos.some((x) => x)) continue;
    tem = true;
    if (p.tipo === 'caixas') {
      for (const x of p.pontos) {
        if (x > 0) max += x;
        else min += x;
      }
    } else {
      max += Math.max(0, ...p.pontos);
      min += Math.min(0, ...p.pontos);
    }
  }
  return tem ? { min: arredondarPontos(min), max: arredondarPontos(max) } : null;
}

export const paraFaixaRascunho = (f: FaixaQualificacao, mostrar: (n: number) => string): FaixaRascunho => ({ chave: chave(), min: mostrar(f.min), max: mostrar(f.max), estrelas: f.estrelas });

/**
 * Faixa nova: começa logo depois da última (ou na menor pontuação), até a maior, com uma estrela a mais. RCRM5-ROB-B2: "logo
 * depois" é o passo da menor casa decimal em uso nos pontos e nas faixas (1, 0,1 ou 0,01), sem buraco para 2,5 entre 0 a 2 e 3 a 5.
 */
export function novaFaixa(
  faixas: FaixaRascunho[],
  limites: { min: number; max: number } | null,
  ler: (s: string) => number | null,
  mostrar: (n: number) => string,
  perguntas: Pick<Pergunta, 'pontos'>[] = [],
): FaixaRascunho {
  const fim = faixas.reduce<number | null>((m, f) => {
    const x = ler(f.max);
    return x === null ? m : m === null ? x : Math.max(m, x);
  }, null);
  const emUso = [...perguntas.flatMap((p) => p.pontos), ...faixas.flatMap((f) => [ler(f.min), ler(f.max)]).filter((x): x is number => x !== null), limites?.min ?? 0, limites?.max ?? 0];
  const passo = 10 ** -Math.max(0, ...emUso.map((x) => casas(arredondarPontos(x))));
  const inicio = fim === null ? (limites?.min ?? 0) : arredondarPontos(fim + passo);
  const topo = limites?.max ?? inicio;
  const ultima = faixas.at(-1)?.estrelas ?? 0;
  return { chave: chave(), min: mostrar(inicio), max: mostrar(Math.max(inicio, topo)), estrelas: Math.min(5, ultima + 1) };
}

export interface TextosFaixas {
  semPontos: string;
  semFaixas: string;
  numero: string;
  invertida: string;
  fora: (min: number, max: number) => string;
  sobreposta: string;
}

/** Erros das faixas (só com a opção ligada): `faixas` (geral) e `f<chave>` (a faixa). */
export function validarFaixas(ligado: boolean, perguntas: Pick<Pergunta, 'tipo' | 'pontos'>[], faixas: FaixaRascunho[], ler: (s: string) => number | null, tx: TextosFaixas): Record<string, string> {
  const e: Record<string, string> = {};
  if (!ligado) return e;
  const lim = limitesPontuacao(perguntas);
  if (!lim) {
    e.faixas = tx.semPontos;
    return e;
  }
  if (!faixas.length) {
    e.faixas = tx.semFaixas;
    return e;
  }
  const lidas: { chave: string; min: number; max: number }[] = [];
  for (const f of faixas) {
    const min = ler(f.min);
    const max = ler(f.max);
    if (min === null || max === null) e[`f${f.chave}`] = tx.numero;
    else if (min > max) e[`f${f.chave}`] = tx.invertida;
    else if (min < lim.min || max > lim.max) e[`f${f.chave}`] = tx.fora(lim.min, lim.max);
    else lidas.push({ chave: f.chave, min, max });
  }
  const ordem = [...lidas].sort((a, b) => a.min - b.min || a.max - b.max);
  for (let k = 1; k < ordem.length; k++) if (ordem[k]!.min <= ordem[k - 1]!.max) e[`f${ordem[k]!.chave}`] = tx.sobreposta;
  return e;
}

/** O corpo que a API recebe: as faixas em ordem crescente (a que não dá para ler fica de fora; `validarFaixas` avisa antes). */
export function faixasParaEnvio(faixas: FaixaRascunho[], ler: (s: string) => number | null): FaixaQualificacao[] {
  return faixas
    .flatMap((f) => {
      const min = ler(f.min);
      const max = ler(f.max);
      return min === null || max === null ? [] : [{ min: arredondarPontos(min), max: arredondarPontos(max), estrelas: f.estrelas }];
    })
    .sort((a, b) => a.min - b.min || a.max - b.max);
}

/** Estrelas da faixa em que a pontuação cai; `null` sem pontuação ou fora de toda faixa. */
export function estrelasDaPontuacao(faixas: FaixaQualificacao[], pontos: number | null): number | null {
  if (pontos === null) return null;
  const p = arredondarPontos(pontos);
  return faixas.find((f) => p >= arredondarPontos(f.min) && p <= arredondarPontos(f.max))?.estrelas ?? null;
}
